import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  BookOpen, Clock, CheckCircle, AlertCircle, 
  ArrowRight, Flame, Target, TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const TodaysLessons = () => {
  const { api } = useAuth();
  const location = useLocation();
  const [lessonPlan, setLessonPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchLessonPlan = useCallback(async () => {
    try {
      setLoading(true);
      // Flush any queued completions from offline/failed attempts
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('pending_progress_')) {
            try {
              const queued = JSON.parse(localStorage.getItem(key));
              if (queued?.payload) {
                await api.post('/progress', queued.payload);
                localStorage.removeItem(key);
              }
            } catch (_) { /* leave queued for next attempt */ }
          }
        }
      } catch (_) { /* localStorage unavailable */ }

      const res = await api.get('/lesson-plans/my-lessons');
      setLessonPlan(res.data);
      setError('');
    } catch (err) {
      console.error('Failed to fetch lesson plan:', err);
      setError('Unable to load your lessons');
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Fetch on mount and whenever we navigate back to the dashboard
  useEffect(() => {
    fetchLessonPlan();
  }, [fetchLessonPlan, location.key]);

  const getSubjectColor = (subject) => {
    const colors = {
      maths: 'border-l-amber-500 bg-amber-50',
      english: 'border-l-rose-500 bg-rose-50',
      science: 'border-l-emerald-500 bg-emerald-50'
    };
    return colors[subject] || 'border-l-slate-500 bg-slate-50';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-slate-200 rounded w-1/3"></div>
          <div className="h-20 bg-slate-100 rounded"></div>
          <div className="h-20 bg-slate-100 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <div className="text-center py-4">
          <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  const { todays_lessons = [], incomplete_lessons = [], total_completed = 0, total_assigned = 0 } = lessonPlan || {};
  const allPendingLessons = [...todays_lessons, ...incomplete_lessons];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-5 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-bold text-lg">Today's Learning</h2>
              <p className="text-purple-100 text-sm">Your personalised lessons</p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-amber-300" />
              <span className="text-2xl font-bold">{total_completed}</span>
            </div>
            <p className="text-xs text-purple-200">lessons this week</p>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm text-slate-600">Weekly Progress</span>
          <span className="text-sm font-semibold text-slate-800">
            {total_completed}/{total_assigned} completed
          </span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-2">
          <div 
            className="bg-gradient-to-r from-purple-500 to-indigo-500 h-2 rounded-full transition-all duration-500"
            style={{ width: `${total_assigned > 0 ? (total_completed / total_assigned) * 100 : 0}%` }}
          />
        </div>
      </div>

      {/* Lessons List */}
      <div className="p-5">
        {allPendingLessons.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <h3 className="font-semibold text-slate-800 mb-1">All caught up!</h3>
            <p className="text-slate-500 text-sm">You've completed all your lessons for today</p>
          </div>
        ) : (
          <div className="space-y-3">
            {incomplete_lessons.length > 0 && (
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-medium text-amber-700">
                  {incomplete_lessons.length} lesson{incomplete_lessons.length > 1 ? 's' : ''} from previous days
                </span>
              </div>
            )}

            {allPendingLessons.map((lesson, index) => (
              <Link 
                key={lesson.id}
                to={`/student/lesson/${lesson.lesson_id}`}
                className={`block border-l-4 rounded-lg p-4 hover:shadow-md transition-shadow ${getSubjectColor(lesson.subject)}`}
                data-testid={`todays-lesson-${index}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-slate-500 uppercase">
                        {lesson.subject}
                      </span>
                      {lesson.assigned_date < new Date().toISOString().split('T')[0] && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                          Overdue
                        </span>
                      )}
                    </div>
                    <h4 className="font-semibold text-slate-800">{lesson.lesson_title}</h4>
                    <p className="text-sm text-slate-500 mt-1">{lesson.topic_name}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                </div>
              </Link>
            ))}

            {allPendingLessons.length > 4 && (
              <p className="text-center text-sm text-slate-500 pt-2">
                +{allPendingLessons.length - 4} more lessons
              </p>
            )}
          </div>
        )}

        {/* Performance Tip - Dynamic based on lessons */}
        {lessonPlan && allPendingLessons.length > 0 && (
          <div className="mt-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-4 flex items-start gap-3">
            <TrendingUp className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-emerald-800">Performance Tip</p>
              <p className="text-xs text-emerald-700 mt-1">
                {allPendingLessons.some(l => l.is_remedial) 
                  ? "Focus on the highlighted areas to strengthen your understanding. These lessons are tailored to help you improve!"
                  : allPendingLessons.some(l => l.difficulty === 'hard')
                    ? "Great progress! You've unlocked harder challenges. Keep up the excellent work!"
                    : "Complete your daily lessons to unlock harder challenges and earn more points!"
                }
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TodaysLessons;
